import React from "react";
import { useQuery, gql } from "@apollo/client";
import Chart from "react-apexcharts";
import Loader from "../../Loader";

const GET_EMPLOYEE_AVAILABILITY = gql`
  query getEmployeeAvailability($data: EmployeeAvailabilityFilter!) {
    getEmployeeAvailability(data: $data) {
      status
      message
      data {
        date
        totalStrength
        dummyCount
        onLeaveCount
        availableCount
      }
    }
  }
`;

const StatCard = ({ title, value, subtext, color = "gray" }) => (
  <div className="bg-[#2C2C2E] p-4 rounded-xl flex flex-col justify-center">
    <div className="text-gray-400 text-sm font-medium">{title}</div>
    <div className={`text-2xl font-bold mt-1 text-${color}-400`}>{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);

const EmployeeAvailabilityWidget = ({ filters }) => {
  const { data, loading, error } = useQuery(GET_EMPLOYEE_AVAILABILITY, {
    variables: {
      data: { startDate: filters.startDate, endDate: filters.endDate },
    },
    fetchPolicy: "network-only",
  });

  if (loading) return <div className="p-6"><Loader /></div>;
  if (error) return <div className="text-red-500 p-6">Error loading employee availability</div>;

  const rows = data?.getEmployeeAvailability?.data || [];
  const latest = rows[rows.length - 1];

  const totalAvailableInRange = rows.reduce(
    (acc, r) => acc + (r.availableCount || 0),
    0,
  );
  const avgAvailablePerDay = rows.length
    ? Math.round(totalAvailableInRange / rows.length)
    : 0;

  const categories = rows.map((r) => r.date);
  const chartOptions = {
    chart: { id: "employee-availability", stacked: true },
    colors: ["#22c55e", "#ef4444", "#6b7280"],
    xaxis: {
      categories,
      labels: { style: { colors: "#FFFFFF" } },
    },
    yaxis: {
      labels: { style: { colors: "#FFFFFF" } },
    },
    legend: { labels: { colors: "#FFFFFF" } },
    tooltip: {
      custom: function ({ dataPointIndex }) {
        const day = rows[dataPointIndex];
        if (!day) return "";
        return `<div style="padding:8px;">
          <strong>${day.date}</strong><br/>
          <span>Total Strength: ${day.totalStrength}</span><br/>
          <span>Available: ${day.availableCount}</span><br/>
          <span>On Leave: ${day.onLeaveCount}</span><br/>
          <span>Dummy: ${day.dummyCount}</span>
        </div>`;
      },
    },
  };
  const chartSeries = [
    { name: "Available", data: rows.map((r) => r.availableCount) },
    { name: "On Leave", data: rows.map((r) => r.onLeaveCount) },
    { name: "Dummy", data: rows.map((r) => r.dummyCount) },
  ];

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800">
      <h3 className="text-[#FBB033] font-bold text-xl mb-6">
        Employee Daily Availability
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Total Availability (Range)"
          value={totalAvailableInRange}
          subtext={`avg ${avgAvailablePerDay}/day over ${rows.length} day${rows.length === 1 ? "" : "s"}`}
          color="green"
        />
        <StatCard
          title="Total Strength"
          value={latest?.totalStrength ?? "-"}
          subtext={`as of ${latest?.date ?? "-"}`}
          color="white"
        />
        <StatCard
          title="Available Today"
          value={latest?.availableCount ?? "-"}
          color="green"
        />
        <StatCard
          title="On Leave"
          value={latest?.onLeaveCount ?? "-"}
          color="red"
        />
        <StatCard
          title="Dummy Employees"
          value={latest?.dummyCount ?? "-"}
          color="yellow"
        />
      </div>

      {rows.length === 0 ? (
        <div className="text-gray-500 text-sm">No data for selected range</div>
      ) : (
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="bar"
          width="100%"
          height={350}
        />
      )}
    </div>
  );
};

export default EmployeeAvailabilityWidget;
